import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";

import { getInactiveStudents } from "../../crud/api/activation.crud";

const useStyles = makeStyles(() => ({
  loadingText: { marginLeft: 8 },
}));

// TZ-786 Step 5: per-team drill-down. The list is gated server-side to
// org admins + Zone Super Admin (see Api::V3::ActivationController
// can_see_named_list?) — for a regular coach the endpoint returns 403,
// the CRUD layer surfaces a notice(), and this panel just stays empty.
// Loading is lazy: the page only mounts this when an admin clicks
// "View inactive" on a row, so we don't hammer the API for teams
// nobody's drilling into.

const InactiveStudentsPanel = ({ teamId, teamName }) => {
  const classes = useStyles();
  const [students, setStudents] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setStudents(null);
    getInactiveStudents((json) => {
      // Guard against a stale response landing after the admin
      // already clicked a different team — without this the panel
      // would briefly show the previous team's list while the new
      // request is in flight.
      if (!cancelled) setStudents(json && json.inactive_students);
    }, teamId)
      .catch(() => {
        if (!cancelled) setStudents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [teamId]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" py={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" className={classes.loadingText}>
          Loading inactive students for {teamName}…
        </Typography>
      </Box>
    );
  }

  if (!students || students.length === 0) {
    return (
      <Box py={2}>
        <Typography variant="body2" color="textSecondary">
          No inactive students on {teamName} — everyone on the roster has signed in at least once.
        </Typography>
      </Box>
    );
  }

  return (
    <Box py={2}>
      <Typography variant="subtitle2" gutterBottom>
        Inactive on {teamName} ({students.length})
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {students.map((s) => (
            <TableRow key={s.student_id}>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.email}</TableCell>
              <TableCell>{s.phone}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

InactiveStudentsPanel.propTypes = {
  teamId:   PropTypes.number.isRequired,
  teamName: PropTypes.string.isRequired,
};

export default InactiveStudentsPanel;
